// SC-927 — reading a 429 back as "when may I book again?".
//
// Two different refusals share the status code on POST /reservations/:
//   - hub's per-person cap, which answers `retry_after_seconds` (the rolling
//     24 h window always ends, so the number is exact);
//   - DRF's own per-IP throttle, which answers in prose only.
// Without a horizon the guest sees a red "échec" and retries forever.
import { localeTag } from '@/lib/localeTag';

// Seconds until the caller may retry, or null when the answer carries none.
export function retryAfterSeconds(err: unknown): number | null {
  const data = (err as { data?: Record<string, unknown> } | undefined)?.data;
  const explicit = data?.retry_after_seconds;
  if (typeof explicit === 'number' && explicit > 0) return explicit;
  // DRF's throttle: "Request was throttled. Expected available in 3599
  // seconds." Its `Retry-After` header carries the same number, but
  // fetchBaseQuery does not expose response headers, so read the message.
  const detail = typeof data?.detail === 'string' ? data.detail : '';
  const match = detail.match(/(\d+)\s*seconds?/);
  if (!match) return null;
  const seconds = Number(match[1]);
  return seconds > 0 ? seconds : null;
}

// "dans 4 heures" / "in 20 minutes" / "20 分钟后" — Intl carries the phrasing
// for the five locales, so no per-locale plural keys are needed. Rounded up:
// telling someone to come back a minute early only earns a second refusal.
export function formatRetryDelay(seconds: number, lng: string): string {
  const fmt = new Intl.RelativeTimeFormat(localeTag(lng), { numeric: 'auto' });
  if (seconds >= 3600) return fmt.format(Math.ceil(seconds / 3600), 'hour');
  return fmt.format(Math.max(1, Math.ceil(seconds / 60)), 'minute');
}
